import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery } from '@apollo/client/react';
import { Box, Button, Center, Heading, HStack, Icon, Input, Link, SimpleGrid, Spinner, Text, VStack, Field } from '@chakra-ui/react';
import { ExtractSkillsAndExperiencesFromBioModal } from '@src/components/sco/Account/UserSkillsEditor';
import { AutoResizeTextarea } from '@src/components/sco/AutoResizeTextarea';
import { DeleteIcon } from '@src/components/sco/icons';
import { experienceTypes } from '@src/constants/experience-types';
import { ScoUser, ScoUserExperience } from '@src/graphql-types';
import { gql } from '@src/lib/gql';
import { useCallback, useEffect, useState } from 'react';
import { LuPlus } from 'react-icons/lu';
export const UserExperiencesEditor = props => {
  const $ = _c(20);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [userExperiences, setUserExperiences] = useState(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = gql`
    query {
      viewer {
        currentUser {
          id
          bio
          linkedin
          userExperiences {
            id
            userExperienceId
            accomplishmentDescription
            accomplishmentTypeKeys
            endDate
            experienceTypeKey
            industryKeys
            organizationName
            positionDescription
            positionTitle
            startDate
          }
        }
      }
    }
  `;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const query = useQuery(t1);
  const user = query.data?.viewer?.currentUser || null;
  const queryUserExperiences = user?.userExperiences || null;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setUserExperiences(_temp);
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const addExperience = t2;
  let t3;
  let t4;
  if ($[3] !== queryUserExperiences) {
    t3 = () => {
      if (queryUserExperiences) {
        setUserExperiences(queryUserExperiences);
      }
    };
    t4 = [queryUserExperiences];
    $[3] = queryUserExperiences;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  if ($[6] !== props.refetchTrigger || $[7] !== query) {
    t5 = () => {
      if (props.refetchTrigger) {
        query.refetch();
      }
    };
    $[6] = props.refetchTrigger;
    $[7] = query;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== props.refetchTrigger) {
    t6 = [props.refetchTrigger];
    $[9] = props.refetchTrigger;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  useEffect(t5, t6);
  if (!queryUserExperiences) {
    let t7;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <Center padding="1em"><Spinner /></Center>;
      $[11] = t7;
    } else {
      t7 = $[11];
    }
    return t7;
  }
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = [4, 4, 8];
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Box marginBottom={4}><Heading size="lg">Your Experiences</Heading><Text>Tell us about what you've accomplished.</Text></Box>;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== props || $[15] !== query || $[16] !== user?.bio || $[17] !== user?.linkedin || $[18] !== userExperiences) {
    t9 = <Box bgColor="colorPalette.container" borderRadius="lg" boxShadow="md" padding={t7} width="full">{t8}{userExperiences.length === 0 ? <VStack bgColor="containerSecondary" borderRadius="md" gap={4} justify="center" padding={6}><Text>You haven't added any experiences yet.</Text><HStack><ExtractSkillsAndExperiencesFromBioModal bio={user?.bio} ctaLabel="Detect My Experiences" linkedinUrl={user?.linkedin} onComplete={() => {
            query.refetch();
            if (props.onDetectionCompleted) {
              props.onDetectionCompleted();
            }
          }} /></HStack><Link color="colorPalette.fg/50" fontSize="sm" variant="underline" onClick={addExperience}>Manually Add Your First Experience</Link></VStack> : <VStack align="stretch" gap={4}>{userExperiences.map(_temp3)}<Button alignSelf="center" onClick={addExperience}><LuPlus />Add Another Experience</Button></VStack>}</Box>;
    $[14] = props;
    $[15] = query;
    $[16] = user?.bio;
    $[17] = user?.linkedin;
    $[18] = userExperiences;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  return t9;
};
const sliderMarks = [[10, 'Novice'], [30, 'Learning'], [50, 'Familiar'], [70, 'Proficient'], [90, 'Expert']];
export const UserExperienceEditorItem = (props: {
  index?: number;
  onChange: (userExperience: Partial<ScoUserExperience>) => any;
  userExperience: ScoUserExperience;
}) => {
  const [userExperience, setUserExperience] = useState<Partial<ScoUserExperience>>(props.userExperience);
  const [upsertUserExperience, upsertUserExperienceMutation] = useMutation(gql`
    mutation ($input: UpsertUserExperienceInput!) {
      upsertUserExperience(input: $input) {
        userExperience {
          id
          userExperienceId
          accomplishmentDescription
          accomplishmentTypeKeys
          endDate
          experienceTypeKey
          industryKeys
          organizationName
          positionDescription
          positionTitle
          startDate
        }
      }
    }
  `);
  const updateUserExperience = useCallback((values?: Partial<ScoUserExperience>) => {
    setUserExperience(prev => ({
      ...prev,
      ...values
    }));
  }, [setUserExperience]);
  const onSubmit = useCallback(async (userExperience_0: Partial<ScoUserExperience>) => {
    await upsertUserExperience({
      variables: {
        input: {
          userExperienceId: userExperience_0.userExperienceId,
          accomplishmentDescription: userExperience_0.accomplishmentDescription,
          accomplishmentTypeKeys: userExperience_0.accomplishmentTypeKeys,
          endDate: userExperience_0.endDate,
          experienceTypeKey: userExperience_0.experienceTypeKey,
          industryKeys: userExperience_0.industryKeys,
          organizationName: userExperience_0.organizationName,
          positionDescription: userExperience_0.positionDescription,
          positionTitle: userExperience_0.positionTitle,
          startDate: userExperience_0.startDate
        }
      }
    });
  }, []);
  const restoreUserExperience = useCallback(async (userExperience_1: Partial<ScoUserExperience>) => {
    if (userExperience_1.userExperienceId) {
      await upsertUserExperience({
        variables: {
          input: {
            userExperienceId: userExperience_1.userExperienceId,
            deletedAt: null
          }
        }
      });
    }
    updateUserExperience({
      deletedAt: null
    });
  }, []);
  const deleteUserExperience = useCallback(async (userExperience_2: Partial<ScoUserExperience>) => {
    if (userExperience_2.userExperienceId) {
      await upsertUserExperience({
        variables: {
          input: {
            userExperienceId: userExperience_2.userExperienceId,
            deletedAt: new Date()
          }
        }
      });
    }
    updateUserExperience({
      deletedAt: new Date()
    });
  }, []);
  useEffect(() => {
    props.onChange(userExperience);
  }, [userExperience]);
  if (userExperience.deletedAt) {
    return <Center key={props.index} bgColor={'containerSecondary'} borderRadius={'md'} padding={[4, 4, 6]}>
        <Text>
          Deleted "{userExperience.positionTitle}
          {'" •'}{' '}
          <Link variant={'underline'} onClick={() => restoreUserExperience(userExperience)}>
            Restore
          </Link>
        </Text>
      </Center>;
  }
  return <Box key={props.index} bgColor={'colorPalette.muted/50'} border={'1px solid'} borderColor={'colorPalette.border'} borderRadius={'md'} boxShadow={'sm'} id={`experience-editor-${props.index}`} padding={[4, 4, 6]}>
      <VStack align={'stretch'} gap={6}>
        <Field.Root>
          <Field.Label>Type of Experience:</Field.Label>
          <Box width={'full'}>
            <HStack gap={2} marginY={2} width={'full'}>
              {experienceTypes.map(experienceType => <Button key={experienceType.key} borderRadius={'lg'} borderWidth={'1px'} flex={1} flexDirection={'column'} height={'auto'} padding={'1.5em 1em 1em'} size={'sm'} variant={userExperience.experienceTypeKey === experienceType.key ? 'solid' : 'outline'} _hover={{
              borderWidth: '1px'
            }} onClick={() => updateUserExperience({
              experienceTypeKey: experienceType.key
            })}>
                  <Icon asChild fontSize={'1em'}>
                    <experienceType.icon />
                  </Icon>
                  <span> {experienceType.title}</span>
                </Button>)}
            </HStack>
          </Box>
        </Field.Root>
        <SimpleGrid columns={[1, 1, 2]} gap={4}>
          <Field.Root>
            <Field.Label htmlFor={`exp-organization-${props.index}`}>Organization</Field.Label>
            <Input id={`exp-organization-${props.index}`} placeholder={'Company/Organization'} value={userExperience.organizationName || ''} onChange={e => updateUserExperience({
            organizationName: e.target.value
          })} />
          </Field.Root>
          <Field.Root>
            <Field.Label htmlFor={`exp-position-${props.index}`}>Position</Field.Label>
            <Input id={`exp-position-${props.index}`} placeholder={'Job Title, Position, or Degree'} value={userExperience.positionTitle || ''} onChange={e_0 => updateUserExperience({
            positionTitle: e_0.target.value
          })} />
          </Field.Root>
        </SimpleGrid>

        <Field.Root>
          <Field.Label htmlFor={`exp-position-description-${props.index}`}>Description</Field.Label>
          <AutoResizeTextarea id={`exp-position-description-${props.index}`} minHeight={'3em'} placeholder={'Add any relevant notes describing this position'} size={'sm'} value={userExperience.positionDescription || ''} onChange={e_1 => updateUserExperience({
          positionDescription: e_1.target.value
        })} />
        </Field.Root>

        <Field.Root>
          <Field.Label htmlFor={`exp-accomplishment-description-${props.index}`}>Accomplishments</Field.Label>

          <AutoResizeTextarea id={`exp-accomplishment-description-${props.index}`} minHeight={'3em'} placeholder={'Note any key accomplishments'} size={'sm'} value={userExperience.accomplishmentDescription || ''} onChange={e_2 => updateUserExperience({
          accomplishmentDescription: e_2.target.value
        })} />
          {/*<Wrap marginY={4}>*/}
          {/*  {[1, 2, 3, 4, 5, 6, 7].map(idx => (*/}
          {/*    <Button variant={'outline'}>Thing {idx}</Button>*/}
          {/*  ))}*/}
          {/*</Wrap>*/}
        </Field.Root>

        <HStack justify={'flex-end'}>
          <Button loading={upsertUserExperienceMutation.loading} variant={'cta'} onClick={() => onSubmit(userExperience)}>
            Save Experience
          </Button>
          <Button aria-label={'Delete Experience'} variant={'outline'} onClick={() => deleteUserExperience(userExperience)}>
            <DeleteIcon />
            Delete
          </Button>
        </HStack>
      </VStack>
    </Box>;
};
function _temp(prev) {
  return [...prev, {}];
}
function _temp2() {
  return [];
}
function _temp3(userExperience, idx) {
  return <UserExperienceEditorItem key={idx} index={idx} userExperience={userExperience} onChange={_temp2} />;
}